import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * CoinMarketCap API服务
 * 需要API密钥
 * 支持加密货币价格和历史数据
 */
class CoinMarketCapService {
  private client = axios.create({
    baseURL: 'https://pro-api.coinmarketcap.com/v1',
    timeout: 10000,
  });

  // API密钥（优先级：用户配置 > 环境变量）
  private apiKey: string | null = null;

  constructor() {
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.coinmarketcap && apiKeys.coinmarketcap.trim() !== '') {
        this.apiKey = apiKeys.coinmarketcap;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }
    
    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_COINMARKETCAP_API_KEY || null;
  }

  /**
   * 设置API密钥
   */
  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * 获取加密货币价格
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    try {
      if (!this.apiKey) {
        throw new Error('CoinMarketCap API key is required');
      }

      // CoinMarketCap使用符号而不是ID
      const symbols = ids.map(id => this.convertIdToSymbol(id));
      
      const response = await this.client.get('/cryptocurrency/quotes/latest', {
        params: {
          symbol: symbols.join(','),
          convert: 'USD',
        },
        headers: {
          'X-CMC_PRO_API_KEY': this.apiKey,
        },
      });

      const results: AssetPrice[] = [];
      const data = response.data.data;

      for (const id of ids) {
        const symbol = this.convertIdToSymbol(id);
        const assetData = data[symbol];

        if (assetData && assetData.quote && assetData.quote.USD) {
          const quote = assetData.quote.USD;
          const currentPrice = quote.price || 0;
          const priceChange24h = quote.volume_24h ? (currentPrice * (quote.percent_change_24h || 0)) / 100 : 0;
          const priceChangePercent24h = quote.percent_change_24h || 0;
          const lastUpdated = quote.last_updated 
            ? new Date(quote.last_updated).toISOString()
            : new Date().toISOString();

          results.push({
            id: id.toLowerCase(),
            symbol: symbol,
            name: assetData.name || this.getAssetName(id),
            current_price: currentPrice,
            price_change_24h: priceChange24h,
            price_change_percentage_24h: priceChangePercent24h,
            last_updated: lastUpdated,
            source_url: this.client.defaults.baseURL || 'https://pro-api.coinmarketcap.com/v1',
          });
        }
      }

      return results;
    } catch (error) {
      console.error('CoinMarketCap获取加密货币价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取历史数据
   */
  async getHistoricalData(id: string, days: number = 7): Promise<HistoricalData> {
    try {
      if (!this.apiKey) {
        throw new Error('CoinMarketCap API key is required');
      }

      const symbol = this.convertIdToSymbol(id);
      
      // CoinMarketCap历史数据API需要不同的端点
      const response = await this.client.get(`/cryptocurrency/quotes/historical`, {
        params: {
          symbol: symbol,
          convert: 'USD',
          count: days,
          interval: days <= 1 ? 'hourly' : 'daily',
        },
        headers: {
          'X-CMC_PRO_API_KEY': this.apiKey,
        },
      });

      const quotes = response.data.data[symbol]?.quotes || [];
      
      const prices: [number, number][] = quotes.map((quote: any) => [
        new Date(quote.time_open).getTime(),
        quote.quote?.USD?.close || 0,
      ]);

      const market_caps: [number, number][] = quotes.map((quote: any) => [
        new Date(quote.time_open).getTime(),
        quote.quote?.USD?.market_cap || 0,
      ]);

      const total_volumes: [number, number][] = quotes.map((quote: any) => [
        new Date(quote.time_open).getTime(),
        quote.quote?.USD?.volume_24h || 0,
      ]);

      return {
        prices,
        market_caps,
        total_volumes,
      };
    } catch (error) {
      console.error('CoinMarketCap获取历史数据失败:', error);
      throw error;
    }
  }

  /**
   * 转换ID为符号
   */
  private convertIdToSymbol(id: string): string {
    const symbolMap: Record<string, string> = {
      bitcoin: 'BTC',
      btc: 'BTC',
      ethereum: 'ETH',
      eth: 'ETH',
      binancecoin: 'BNB',
      bnb: 'BNB',
      solana: 'SOL',
      sol: 'SOL',
      cardano: 'ADA',
      ada: 'ADA',
      ripple: 'XRP',
      xrp: 'XRP',
      dogecoin: 'DOGE',
      doge: 'DOGE',
      polkadot: 'DOT',
      dot: 'DOT',
      polygon: 'MATIC',
      matic: 'MATIC',
      avalanche: 'AVAX',
      'avalanche-2': 'AVAX',
      avax: 'AVAX',
      chainlink: 'LINK',
      link: 'LINK',
      uniswap: 'UNI',
      uni: 'UNI',
      litecoin: 'LTC',
      ltc: 'LTC',
      cosmos: 'ATOM',
      atom: 'ATOM',
    };

    return symbolMap[id.toLowerCase()] || id.toUpperCase();
  }

  /**
   * 获取资产名称
   */
  private getAssetName(id: string): string {
    const nameMap: Record<string, string> = {
      bitcoin: 'Bitcoin',
      btc: 'Bitcoin',
      ethereum: 'Ethereum',
      eth: 'Ethereum',
      binancecoin: 'BNB',
      bnb: 'BNB',
      solana: 'Solana',
      sol: 'Solana',
      cardano: 'Cardano',
      ada: 'Cardano',
      ripple: 'XRP',
      xrp: 'XRP',
      dogecoin: 'Dogecoin',
      doge: 'Dogecoin',
      polkadot: 'Polkadot',
      dot: 'Polkadot',
      polygon: 'Polygon',
      matic: 'Polygon',
      avalanche: 'Avalanche',
      'avalanche-2': 'Avalanche',
      avax: 'Avalanche',
      chainlink: 'Chainlink',
      link: 'Chainlink',
      uniswap: 'Uniswap',
      uni: 'Uniswap',
      litecoin: 'Litecoin',
      ltc: 'Litecoin',
      cosmos: 'Cosmos',
      atom: 'Cosmos',
    };

    return nameMap[id.toLowerCase()] || id.charAt(0).toUpperCase() + id.slice(1).toLowerCase();
  }
}

export const coinmarketcapService = new CoinMarketCapService();
